// manages requests for availability
export default class AvailabilityQueue {

  static get defaults() {
    return {
      // max batch size
      batchSize: 5,
      // max concurrent requests
      concurrency: 3,
      // place_id
      place_id: null,
      // debounce wait time
      wait: 500
    };
  }

  /**
   * AvailabilityQueue constructor
   * @method constructor
   * @param  {Object}    [opts={}] – see `defaults`
   * @return {AvailabilityQueue}
   */

  constructor( opts = {} ) {
    this._options = Object.assign( {}, AvailabilityQueue.defaults, opts );

    // pending lazy-load days
    this._lazy = [];
    // pending on-demand days
    this._urgent = [];
    // debounce timer for _maybeDequeue
    this._dequeueTimer = null;
    // inflight request count
    this._inflight = 0;
  }

  /**
   * Schedule a dequeue (delayed so we can combine days into a single request)
   * @method _scheduleDequeue
   * @return {undefined}
   */

  _scheduleDequeue() {
    clearTimeout( this._dequeueTimer );

    this._dequeueTimer = setTimeout( () => {
      this._maybeDequeue();
    }, this._options.wait );
  }

  /**
   * Get an array of AvailabilityDay objects for our next request
   * @method _getRequestDays
   * @return {Array<AvailabilityDay>}
   */

  _getRequestDays() {
    const { _urgent, _lazy, _options: { batchSize } } = this;

    if ( !_lazy.length && !_urgent.length ) {
      return [];
    }

    // one day
    const day = 1000 * 60 * 60 * 24;
    // first queue item
    const first = _urgent.length ? _urgent.shift() : _lazy.shift();
    // max date we can fit into this request
    const max = new Date( first.start.getTime() + day * batchSize );
    // filter dates that are within bounds
    const filter = d => d.start >= first.start && d.finish <= max;
    // urgent requests
    const urgents = _urgent.filter( filter );
    // lazy requests
    const lazies = _lazy.filter( filter );
    // remove an AvailabilityDay from the given queue
    const remove = ( a, i ) => a.splice( a.indexOf( i ), 1 );
    // sort by start date
    const sort = ( ( a, b ) => a.start - b.start );

    // remove any duplicate requests in the urgent queue
    urgents.forEach( item => remove( _urgent, item ) );

    // remove any duplicate requests in the lazy queue
    lazies.forEach( item => remove( _lazy, item ) );

    return [ first ].concat( urgents ).concat( lazies ).sort( sort );
  }

  /**
   * Dequeue pending days if there are any
   * @method _maybeDequeue
   * @return {undefined}
   */

  _maybeDequeue() {
    while ( this._inflight < this._options.concurrency ) {
      // get the next batch of AvailabilityDays to fetch
      const days = this._getRequestDays();

      if ( !days.length ) {
        return;
      }

      this._inflight++;

      const start   = days[ 0 ].start;
      const finish  = days[ days.length - 1 ].finish;
      const success = this._onSuccess.bind( this, days );
      const error   = this._onError.bind( this, days );

      days.forEach( day => day.setLoading() );

      this._getSlots( start, finish ).then( success ).catch( error );
    }
  }

  /**
   * Handle successful slot response
   * @method _onSuccess
   * @param  {Array<AvailabilityDay>} days  – array of days in request
   * @param  {Object}                 slots – { slots: [], duration: Number }
   * @return {undefined}
   */

  _onSuccess( days, slots ) {
    this._inflight--;

    for ( const day of days ) {
      day.maybeAddSlots( slots );
      day.setLoaded();
    }

    this._scheduleDequeue();
  }

  /**
   * Handle unsuccessful slot response
   * @method _onError
   * @param  {Array<AvailabilityDay>} days – array of days in request
   * @param  {Error>}                 err  – error object
   * @return {undefined}
   */

  _onError( days, err ) {
    this._inflight--;

    for ( const day of days ) {
      day.setErrored( err );
    }

    this._scheduleDequeue();
  }

  /**
   * get slots
   * @method _getSlots
   * @param  {Date}  start  – start of requested range
   * @param  {Date}  finish – end of requested range
   * @return {Promise<Array<Array<String>>>} Promise w/ array of FSL slots
   */

  _getSlots( start, finish ) {
    if ( !this._options.place_id ) {
      return Promise.reject( new Error('No place_id set') );
    }

    return new Promise( ( resolve, reject ) => {
      const xhr = new XMLHttpRequest();

      start  = new Date( start ).getTime();
      finish = new Date( finish ).getTime();

      const url = new URL( location.href );
      const sameday = !!( url.searchParams.get('sameday') );

      const { place_id } = this._options;
      const route = `${ window.cloudURL }/places/${ place_id }/slots`;
      const query = `?start=${ start }&finish=${ finish }&sameday=${ sameday }`;

      xhr.open( 'GET', `${ route }${ query }` );
      xhr.onload = () => {
        if ( xhr.status !== 200 ) {
          return reject( JSON.parse( xhr.responseText ) );
        }
        resolve( JSON.parse( xhr.responseText ) );
      };
      xhr.send();
    });
  }

  /**
   * Low-priority request to pre-load an AvailabilityDay
   * @method lazy
   * @param  {AvailabilityDay} day
   * @return {Promise<Array<AvailabilitySlot>>}
   */

  lazy( day ) {
    console.log( 'LAZY LOAD REQUEST: %s', day.key );

    const lazyIndex   = this._lazy.indexOf( day );
    const urgentIndex = this._urgent.indexOf( day );
    const queued      = lazyIndex !== -1 || urgentIndex !== -1;

    // not already queued? add it
    if ( !queued ) {
      this._lazy.push( day );
      this._scheduleDequeue();
    }

    return day._promise;
  }

  /**
   * High-priority request to pre-load an AvailabilityDay
   * @method lazy
   * @param  {AvailabilityDay} day
   * @return {Promise<Array<AvailabilitySlot>>}
   */

  urgent( day ) {
    console.log( 'URGENT LOAD REQUEST: %s', day.key );

    const lazyIndex   = this._lazy.indexOf( day );
    const urgentIndex = this._urgent.indexOf( day );

    // already in the lazy queue? remove it
    if ( lazyIndex !== -1 ) {
      this._lazy.splice( lazyIndex, 1 );
    }

    // already in the urgent queue? remove it,
    // we'll re-add it with higher priority in a second
    if ( urgentIndex !== -1 ) {
      this._urgent.splice( urgentIndex, 1 );
    }

    this._urgent.unshift( day );
    this._scheduleDequeue();

    return day._promise;
  }

  /**
   * Flush the queue
   * @method flush
   * @return {undefined}
   */

  flush() {
    clearTimeout( this._dequeueTimer );
    this._lazy.length = this._urgent.length = 0;
  }

}
